import type { WasmExportConfig } from "../types/WasmExportConfig"
import type { WasmInstance } from "../types/WasmInstance"
import type { WasmBinary } from "../utils/ResolveWasm"
import { CreateSingleton, LockManager, RecordMap } from "@pdfking/tfunc"
import { CreateWasmLoader } from "./CreateWasmLoader"
import { CreateWasmSession } from "./CreateWasmSession"

type Locked<T> = T extends (i: WasmInstance, ...args: infer A) => infer R
  ? (...args: A) => Promise<R>
  : never

export type ProvisionAPI<F> = {
  [K in keyof F]: Locked<F[K]>
} & {
  readonly instance: WasmInstance
}

export type ProvisionSettings = {
  readonly config?: Partial<WasmExportConfig>
  readonly init?: (instance: WasmInstance) => void
}

export const CreateProvisionWasm = <F extends Record<string, any>>(
  binary: WasmBinary,
  settings: ProvisionSettings,
  functions: F
) => {
  const loader = CreateSingleton(() =>
    CreateWasmLoader(binary, settings.config)
  )

  const { schedule } = LockManager()

  const session = CreateWasmSession(async () => {
    const create = await loader()
    const instance = await create()
    settings.init?.(instance)
    return instance
  })

  const createAPI = (instance: WasmInstance): ProvisionAPI<F> => {
    const api = RecordMap(
      (func: any) =>
        (...args: any[]) =>
          schedule("λ", async () => func(instance, ...args)),
      functions
    ) as unknown as Omit<ProvisionAPI<F>, "instance">

    return { instance, ...api } as ProvisionAPI<F>
  }

  return async function* ProvisionWasm<T>(
    fn: (api: ProvisionAPI<F>) => AsyncGenerator<T>
  ): AsyncGenerator<T> {
    yield* session(async function* (instance: WasmInstance) {
      yield* fn(createAPI(instance))
    })
  }
}
